/* TAPE! first-party wishlist — guest localStorage + authenticated V1 sync.
   Notify Me is presented as coming soon and is completely disabled:
   no subscriptions, no handlers, no delivery. */
(function () {
  'use strict';

  var STORAGE_GUEST = 'tape:wishlist:v1';
  var STORAGE_CUSTOMER_PREFIX = 'tape:wishlist:c:';
  var boot = window.TAPE_WISHLIST || {};
  var customerId = boot.customerId ? String(boot.customerId) : '';
  var apiBase = boot.apiBase || '/apps/tape/wishlist';
  var pageUrl = boot.pageUrl || '/pages/wishlist';
  var money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });

  var ORDERABLE = { in_stock: true, preorder: true, supplier: true };

  function storageKey() {
    return customerId ? STORAGE_CUSTOMER_PREFIX + customerId : STORAGE_GUEST;
  }

  function readLocal() {
    try {
      var raw = localStorage.getItem(storageKey());
      var data = raw ? JSON.parse(raw) : { version: 1, items: [], alerts: {}, consent: null };
      if (!data.items) data.items = [];
      if (!data.alerts) data.alerts = {};
      return data;
    } catch (e) {
      return { version: 1, items: [], alerts: {}, consent: null };
    }
  }

  function writeLocal(data) {
    if (!data.alerts) data.alerts = {};
    localStorage.setItem(storageKey(), JSON.stringify(data));
    window.dispatchEvent(new CustomEvent('tape:wishlist:change', { detail: data }));
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Wishlist V1 (Postgres, token-allowlisted) is the authenticated source of
     truth: /v1/items + /v1/merge under the app proxy. The legacy proxy-root
     metafield payload is NOT used for account sync (it never reached the
     V1 database, so lists were device-local only). localStorage remains a
     per-device display cache; server membership wins on pull, and locally
     added items that have not been confirmed carry _pending until the
     server acknowledges them so a failed request never loses a save. */
  var apiItems = apiBase + '/v1/items';
  var apiMerge = apiBase + '/v1/merge';
  /* null = unknown, true = last remote call succeeded, false = rejected/failed.
     Used so the wishlist page does not claim account sync when the backend
     refused or the network failed (items may still be device-local / pending). */
  var remoteSyncOk = null;

  function markRemoteSync(ok) {
    remoteSyncOk = !!ok;
    paintSyncStatus();
  }

  function hasPendingRemoteWork(data) {
    data = data || readLocal();
    if ((data.pending_deletes || []).length) return true;
    return (data.items || []).some(function (i) { return i && i._pending; });
  }

  function paintSyncStatus() {
    var el = document.querySelector('[data-tape-wishlist-sync]');
    if (!el) return;
    if (!customerId || !boot.enableRemote) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    if (remoteSyncOk === false || (remoteSyncOk === null && hasPendingRemoteWork())) {
      el.hidden = false;
      el.textContent = hasPendingRemoteWork()
        ? 'Account sync is pending or failed. Editions on this device are saved locally until sync succeeds — refresh to retry.'
        : 'Could not reach your account wishlist. Showing this device only — refresh to retry.';
      return;
    }
    el.hidden = true;
    el.textContent = '';
  }

  function serverEntry(item) {
    return {
      variant_id: String(item.variant_id || ''),
      product_id: String(item.product_id || ''),
      title: item.title || '',
      source: item.source || 'storefront'
    };
  }

  function remoteFetch(url, options) {
    var opts = options || {};
    opts.credentials = 'same-origin';
    opts.headers = Object.assign({ Accept: 'application/json' }, opts.headers || {});
    return fetch(url, opts).then(function (r) {
      if (!r.ok) throw new Error('wishlist_remote_' + r.status);
      return r.json();
    });
  }

  function mergeGuestIntoCustomer() {
    if (!customerId) return;
    try {
      var guestRaw = localStorage.getItem(STORAGE_GUEST);
      if (!guestRaw) return;
      var guest = JSON.parse(guestRaw);
      if (!guest.items || !guest.items.length) return;
      var dest = readLocal();
      var seen = {};
      dest.items.forEach(function (i) { seen[String(i.variant_id)] = true; });
      guest.items.forEach(function (i) {
        if (!seen[String(i.variant_id)]) {
          if (customerId && boot.enableRemote) i._pending = true;
          dest.items.push(i);
          seen[String(i.variant_id)] = true;
        }
      });
      if (guest.alerts) {
        Object.keys(guest.alerts).forEach(function (k) {
          if (!dest.alerts[k]) dest.alerts[k] = guest.alerts[k];
        });
      }
      writeLocal(dest);
      /* Clear guest entries ONLY after successful server confirmation.
         On failure the guest copy is retained so the merge can retry;
         re-merging is safe because entries dedupe by variant_id. */
      if (customerId && boot.enableRemote) {
        syncRemote(dest).then(function (ok) {
          if (ok) {
            localStorage.removeItem(STORAGE_GUEST);
            var after = readLocal();
            after.items.forEach(function (i) { delete i._pending; });
            writeLocal(after);
          }
        });
      } else {
        localStorage.removeItem(STORAGE_GUEST);
      }
    } catch (e) { /* ignore */ }
  }

  /* Union-sync local items to the server (guest merge / retry path).
     Never deletes: removals go through DELETE /v1/items/{variant_id}. */
  function syncRemote(data) {
    if (!customerId || !boot.enableRemote) return Promise.resolve(false);
    var items = (data.items || []).map(serverEntry).filter(function (i) { return i.variant_id; });
    if (!items.length) return Promise.resolve(true);
    return remoteFetch(apiMerge, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function () { markRemoteSync(true); return true; })
      .catch(function () { markRemoteSync(false); return false; });
  }

  function addRemote(item) {
    return remoteFetch(apiItems, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(serverEntry(item))
    }).then(function (body) { markRemoteSync(true); return body; })
      .catch(function (err) { markRemoteSync(false); throw err; });
  }

  function removeRemote(variantId) {
    return remoteFetch(apiItems + '/' + encodeURIComponent(String(variantId)), { method: 'DELETE' })
      .then(function (body) { markRemoteSync(true); return body; })
      .catch(function (err) { markRemoteSync(false); throw err; });
  }

  /* Flush locally-pending adds/deletes, then pull the authoritative server
     list. Server membership defines the account list on every device, and
     unconfirmed (_pending) saves are preserved. Display fields are NOT
     taken from the local snapshot as current data — hydrateWishlist()
     replaces them with Shopify's live product data after this pull. */
  function pullRemote() {
    if (!customerId || !boot.enableRemote) return Promise.resolve(null);
    var local = readLocal();
    var pendingAdds = local.items.filter(function (i) { return i._pending; });
    var pendingDeletes = (local.pending_deletes || []).slice();
    var flush = Promise.resolve();
    if (pendingAdds.length) {
      flush = flush.then(function () { return syncRemote(local); }).then(function (ok) {
        if (ok) {
          var d = readLocal();
          d.items.forEach(function (i) { delete i._pending; });
          writeLocal(d);
        }
      });
    }
    pendingDeletes.forEach(function (vid) {
      flush = flush.then(function () { return removeRemote(vid); }).then(function () {
        var d = readLocal();
        d.pending_deletes = (d.pending_deletes || []).filter(function (x) { return String(x) !== String(vid); });
        writeLocal(d);
      }).catch(function () { /* stays queued for next load */ });
    });
    return flush.then(function () {
      return remoteFetch(apiItems, { method: 'GET' });
    }).then(function (remote) {
      markRemoteSync(true);
      if (!remote || !remote.items) return null;
      var current = readLocal();
      var cache = {};
      current.items.forEach(function (i) { cache[String(i.variant_id)] = i; });
      var deleted = {};
      (current.pending_deletes || []).forEach(function (vid) { deleted[String(vid)] = true; });
      var items = [];
      remote.items.forEach(function (srv) {
        var k = String(srv.variant_id);
        if (deleted[k]) return;
        var enrich = cache[k] || {};
        items.push(Object.assign({}, enrich, {
          product_id: String(srv.product_id || enrich.product_id || ''),
          variant_id: k,
          title: enrich.title || srv.title_snapshot || srv.title || '',
          added_at: srv.added_at || enrich.added_at || '',
          source: srv.source || enrich.source || 'storefront'
        }));
      });
      current.items.forEach(function (i) {
        if (i._pending && !items.some(function (x) { return String(x.variant_id) === String(i.variant_id); })) {
          items.push(i);
        }
      });
      current.items = items;
      writeLocal(current);
      return current;
    }).catch(function () { markRemoteSync(false); return null; });
  }

  /* --- Shopify-authoritative hydration -----------------------------------
     PostgreSQL (signed in) / localStorage (guest) decides MEMBERSHIP only.
     Display fields come from Shopify at load: one batched Section Rendering
     request per 20 products (the product-compare pattern:
     /search?section_id=tape-wishlist-hydration&q=id:.. OR id:..), keyed by
     variant_id so editions of the same film never merge. Local snapshots
     are never treated as current for signed-in users; they only paint until
     hydration lands. A product Shopify no longer returns (archived, deleted,
     unpublished, variant removed) gets a safe unavailable state — stale
     fields are cleared and nothing is invented. A failed hydration request
     changes nothing, so a network error can never mass-mark a list
     unavailable. */
  var HYDRATION_SECTION = 'tape-wishlist-hydration';
  var HYDRATION_BATCH = 20;
  var hydrationKey = '';
  var hydrationPromise = null;

  function fetchHydrationBatch(productIds) {
    var q = productIds.map(function (id) { return 'id:' + id; }).join(' OR ');
    var base = (window.routes && window.routes.search_url) || '/search';
    var url = base + '?section_id=' + HYDRATION_SECTION + '&type=product&options%5Bunavailable_products%5D=show&q=' + encodeURIComponent(q);
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(function (r) { if (!r.ok) throw new Error('wishlist_hydrate_' + r.status); return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var node = doc.querySelector('[data-tape-wishlist-hydration]');
        /* No payload node means the hydration section did not render —
           a failed batch, NOT "every product is gone". Throwing keeps the
           caller's failed-batch path (items left untouched). */
        if (!node) throw new Error('wishlist_hydrate_no_payload');
        var parsed = JSON.parse(node.textContent || '[]');
        return Array.isArray(parsed) ? parsed : [];
      });
  }

  function hydrateWishlist() {
    var items = readLocal().items || [];
    if (!items.length) return Promise.resolve(false);
    var key = items.map(function (i) { return String(i.variant_id); }).sort().join(',');
    if (key === hydrationKey) return Promise.resolve(true);
    if (hydrationPromise) return hydrationPromise;
    var productIds = [];
    var seenPid = {};
    items.forEach(function (i) {
      var pid = String(i.product_id || '');
      if (pid && !seenPid[pid]) { seenPid[pid] = true; productIds.push(pid); }
    });
    var batches = [];
    for (var b = 0; b < productIds.length; b += HYDRATION_BATCH) {
      batches.push(productIds.slice(b, b + HYDRATION_BATCH));
    }
    var queried = {}; /* product_ids covered by a SUCCESSFUL batch */
    var byVariant = {};
    function remember(records) {
      (records || []).forEach(function (rec) {
        if (rec && rec.variant_id != null) byVariant[String(rec.variant_id)] = rec;
      });
    }
    var chain = Promise.resolve();
    batches.forEach(function (batch) {
      chain = chain.then(function () {
        return fetchHydrationBatch(batch).then(function (records) {
          batch.forEach(function (pid) { queried[pid] = true; });
          remember(records);
        }).catch(function () { /* failed batch: leave those items untouched */ });
      });
    });
    /* Storefront search returns a short page. A product omitted from a batch
       is looked up on its own before it can be called unavailable. */
    chain = chain.then(function () {
      var data = readLocal();
      var missing = [];
      var seenMiss = {};
      (data.items || []).forEach(function (item) {
        var pid = String(item.product_id || '');
        if (!pid || !queried[pid] || byVariant[String(item.variant_id)] || seenMiss[pid]) return;
        seenMiss[pid] = true;
        missing.push(pid);
      });
      var again = Promise.resolve();
      missing.forEach(function (pid) {
        again = again.then(function () {
          return fetchHydrationBatch([pid]).then(function (records) {
            remember(records);
          }).catch(function () { queried[pid] = false; });
        });
      });
      return again;
    });
    hydrationPromise = chain.then(function () {
      var data = readLocal();
      var changed = false;
      data.items.forEach(function (item) {
        var rec = byVariant[String(item.variant_id)];
        if (rec) {
          item.product_id = String(rec.product_id || item.product_id || '');
          item.handle = rec.handle || '';
          item.title = rec.title || item.title || '';
          item.url = rec.url || '';
          item.image = rec.image || '';
          item.format = rec.format || '';
          item.label = rec.label || '';
          item.region = rec.region || '';
          item.edition = rec.edition || '';
          item.price_cents = (rec.price_cents === null || rec.price_cents === undefined) ? '' : rec.price_cents;
          item.purchase_state = rec.purchase_state || '';
          item.state_label = rec.state_label || '';
          item.cta_label = rec.cta_label || '';
          item.release_display = rec.release_display || '';
          item.release_iso = rec.release_iso || '';
          item.date_status = rec.date_status || '';
          item._unavailable = false;
          item._hydrated = true;
          changed = true;
        } else if (queried[String(item.product_id || '')] || !item.product_id) {
          /* Shopify was asked and did not return this edition: show the
             saved title with a safe unavailable state, invent nothing. */
          item.handle = ''; item.url = ''; item.image = '';
          item.format = ''; item.label = ''; item.region = ''; item.edition = '';
          item.price_cents = ''; item.purchase_state = ''; item.state_label = '';
          item.cta_label = ''; item.release_display = ''; item.release_iso = '';
          item.date_status = '';
          item._unavailable = true;
          item._hydrated = true;
          changed = true;
        }
      });
      hydrationKey = key;
      hydrationPromise = null;
      if (changed) writeLocal(data);
      return changed;
    });
    return hydrationPromise;
  }

  function hasVariant(variantId) {
    var id = String(variantId);
    return readLocal().items.some(function (i) { return String(i.variant_id) === id; });
  }

  function snapshotFromButton(btn) {
    return {
      product_id: btn.getAttribute('data-product-id') || '',
      variant_id: btn.getAttribute('data-variant-id') || '',
      handle: btn.getAttribute('data-handle') || '',
      title: btn.getAttribute('data-title') || '',
      url: btn.getAttribute('data-url') || '',
      image: btn.getAttribute('data-image') || '',
      format: btn.getAttribute('data-format') || '',
      label: btn.getAttribute('data-label') || '',
      region: btn.getAttribute('data-region') || '',
      edition: btn.getAttribute('data-edition') || '',
      price_cents: btn.getAttribute('data-price-cents') || '',
      purchase_state: btn.getAttribute('data-purchase-state') || '',
      state_label: btn.getAttribute('data-state-label') || '',
      cta_label: btn.getAttribute('data-cta-label') || '',
      release_display: btn.getAttribute('data-release-display') || '',
      release_iso: btn.getAttribute('data-release-iso') || '',
      date_status: btn.getAttribute('data-date-status') || '',
      source: 'heart'
    };
  }

  function toggle(item) {
    var data = readLocal();
    var id = String(item.variant_id);
    var idx = data.items.findIndex(function (i) { return String(i.variant_id) === id; });
    var added;
    var savedItem = null;
    if (idx >= 0) {
      data.items.splice(idx, 1);
      if (data.alerts) delete data.alerts[id];
      added = false;
    } else {
      data.items.push({
        product_id: String(item.product_id || ''),
        variant_id: id,
        handle: item.handle || '',
        title: item.title || '',
        url: item.url || '',
        image: item.image || '',
        format: item.format || '',
        label: item.label || '',
        region: item.region || '',
        edition: item.edition || '',
        price_cents: item.price_cents || '',
        purchase_state: item.purchase_state || '',
        state_label: item.state_label || '',
        cta_label: item.cta_label || '',
        release_display: item.release_display || '',
        release_iso: item.release_iso || '',
        date_status: item.date_status || '',
        added_at: new Date().toISOString(),
        source: item.source || 'storefront'
      });
      savedItem = data.items[data.items.length - 1];
      if (customerId && boot.enableRemote) savedItem._pending = true;
      added = true;
    }
    writeLocal(data);
    if (customerId && boot.enableRemote) {
      if (added && savedItem) {
        addRemote(savedItem).then(function () {
          var d = readLocal();
          d.items.forEach(function (i) { if (String(i.variant_id) === id) delete i._pending; });
          writeLocal(d);
        }).catch(function () { /* stays _pending; retried on next load */ });
      } else if (!added) {
        removeRemote(id).catch(function () {
          var d = readLocal();
          if (!d.pending_deletes) d.pending_deletes = [];
          if (d.pending_deletes.map(String).indexOf(id) === -1) d.pending_deletes.push(id);
          writeLocal(d);
        });
      }
    }
    return added;
  }

  function paintButtons(root) {
    (root || document).querySelectorAll('[data-tape-wish]').forEach(function (btn) {
      var vid = btn.getAttribute('data-variant-id');
      var on = hasVariant(vid);
      var title = btn.getAttribute('data-title') || 'item';
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.classList.toggle('is-on', on);
      btn.style.setProperty('--tape-wish-fill', on ? 'currentColor' : 'none');
      btn.setAttribute('aria-label', (on ? 'Remove ' : 'Add ') + title + (on ? ' from wishlist' : ' to wishlist'));
      var label = btn.querySelector('[data-tape-wish-label]');
      if (label) label.textContent = on ? 'Saved' : 'Wishlist';
    });
    var countEls = document.querySelectorAll('[data-tape-wishlist-count]');
    var n = readLocal().items.length;
    countEls.forEach(function (el) {
      el.textContent = n ? String(n) : '';
      el.hidden = !n;
    });
  }

  function formatPrice(cents) {
    if (cents === '' || cents == null) return '';
    var n = Number(cents);
    if (isNaN(n)) return '';
    return money.format(n / 100);
  }

  function metaLine(item) {
    return [item.format, item.label, item.region].filter(Boolean).join(' · ');
  }

  function bellSvg() {
    return '<svg class="tape-wl-alert__bell" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 2h-15Z" fill="var(--tape-bell-fill, none)" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>' +
      '<path d="M10 20.5a2.2 2.2 0 0 0 4 0" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
  }

  /* Notify Me — coming soon, completely disabled. The button is a native
     disabled control (no click can fire, no handler is registered for it),
     and the note underneath is its accessible description. Nothing here
     creates a subscription or emits an event. */
  function alertWidget(item) {
    var id = String(item.variant_id);
    var noteId = 'tape-wl-soon-' + id;
    return '<div class="tape-wl-alert tape-wl-alert--soon" data-tape-alert="' + esc(id) + '">' +
      '<button class="tape-wl-alert__toggle tape-wl-alert__toggle--soon" type="button" disabled aria-disabled="true" aria-describedby="' + esc(noteId) + '">' +
      bellSvg() + '<span>Notify Me</span>' +
      '<span class="visually-hidden"> for ' + esc(item.title || 'this edition') + '</span></button>' +
      '<p class="tape-wl-alert__soon" id="' + esc(noteId) + '">Notifications coming shortly.</p>' +
      '</div>';
  }

  function rowHtml(item) {
    var id = String(item.variant_id);
    var unavailable = item._unavailable === true;
    var href = unavailable ? '' : (item.url || (item.handle ? '/products/' + item.handle : ''));
    var img = item.image
      ? '<img src="' + esc(item.image) + '" alt="" width="96" height="144" loading="lazy" decoding="async">'
      : '<span class="tape-wl-row__ph" aria-hidden="true"></span>';
    var meta = metaLine(item);
    var edition = item.edition || '';
    var release = item.release_display || '';
    if (!release && item.date_status === 'tbc') release = 'TBC';
    var price = formatPrice(item.price_cents);
    var state = unavailable ? 'unavailable' : (item.purchase_state || '');
    var stateLabel = unavailable
      ? 'No longer available'
      : (item.state_label || (state ? state.replace(/_/g, ' ') : 'See product page'));
    var orderable = ORDERABLE[state];
    var cta = '';
    if (!unavailable && orderable && item.cta_label) {
      cta = '<a class="tape-btn tape-btn--ink tape-btn--sm tape-wl-row__cta" href="' + esc(href) + '">' + esc(item.cta_label) +
        '<span class="visually-hidden">: ' + esc(item.title || '') + '</span></a>';
    } else if (!unavailable && state !== 'sold_out' && state !== 'unconfirmed' && href) {
      cta = '<a class="tape-btn tape-btn--outline tape-btn--sm tape-wl-row__cta" href="' + esc(href) + '">View</a>';
    }
    var coverHtml = href
      ? '<a class="tape-wl-row__cover" href="' + esc(href) + '" tabindex="-1" aria-hidden="true">' + img + '</a>'
      : '<span class="tape-wl-row__cover">' + img + '</span>';
    var titleHtml = href
      ? '<a href="' + esc(href) + '">' + esc(item.title || 'Edition') + '</a>'
      : esc(item.title || 'Edition');

    var dateHtml = release
      ? '<p class="tape-wl-row__date' + (item.date_status === 'tbc' ? ' tape-wl-row__date--muted' : '') + '">' +
        (item.date_status === 'tbc' ? 'Release date TBC' : ('Out ' + esc(release))) + '</p>'
      : '';

    return (
      '<article class="tape-wl-row" role="listitem" data-variant="' + esc(id) + '" data-state="' + esc(state) + '">' +
        coverHtml +
        '<div class="tape-wl-row__main">' +
          (meta ? '<p class="tape-wl-row__meta">' + esc(meta) + '</p>' : '') +
          '<h2 class="tape-wl-row__title">' + titleHtml + '</h2>' +
          (edition ? '<p class="tape-wl-row__edition">' + esc(edition) + '</p>' : '') +
          dateHtml +
        '</div>' +
        '<div class="tape-wl-row__buy">' +
          (price ? '<p class="tape-wl-row__price">' + esc(price) + '</p>' : '') +
          '<p class="tape-wl-row__state" data-state="' + esc(state) + '"><span class="tape-wl-row__state-dot" aria-hidden="true"></span>' +
            esc(stateLabel) + '</p>' +
          '<div class="tape-wl-row__actions">' + cta + '</div>' +
        '</div>' +
        '<div class="tape-wl-row__extra">' + (unavailable ? '' : alertWidget(item)) +
          '<button type="button" class="tape-wl-remove" data-tape-wish-remove="' + esc(id) + '">Remove' +
          '<span class="visually-hidden"> ' + esc(item.title || '') + ' from wishlist</span></button>' +
        '</div>' +
      '</article>'
    );
  }

  function sortItems(items, mode) {
    var copy = items.slice();
    if (mode === 'title') {
      copy.sort(function (a, b) {
        return String(a.title || '').localeCompare(String(b.title || ''), 'en', { sensitivity: 'base' });
      });
    } else if (mode === 'release') {
      copy.sort(function (a, b) {
        var x = a.release_iso || (a.date_status === 'tbc' ? '9999-12-31' : '9999');
        var y = b.release_iso || (b.date_status === 'tbc' ? '9999-12-31' : '9999');
        if (x.slice(5) === '12-31') x = '9999-12-31';
        if (y.slice(5) === '12-31') y = '9999-12-31';
        return x < y ? -1 : x > y ? 1 : 0;
      });
    } else {
      copy.sort(function (a, b) {
        return String(b.added_at || '').localeCompare(String(a.added_at || ''));
      });
    }
    return copy;
  }

  function renderPage() {
    var root = document.querySelector('[data-tape-wishlist-page]');
    if (!root) return;
    var data = readLocal();
    var empty = root.querySelector('[data-tape-wishlist-empty]');
    var list = root.querySelector('[data-tape-wishlist-list]');
    var toolbar = root.querySelector('[data-tape-wishlist-toolbar]');
    var summary = root.querySelector('[data-tape-wishlist-summary]');
    var countEl = root.querySelector('[data-tape-wishlist-toolbar-count]');
    var banner = root.querySelector('[data-tape-wishlist-notify-banner]');
    var sortEl = root.querySelector('[data-tape-wishlist-sort]');
    var mode = sortEl ? sortEl.value : 'added';
    var items = sortItems(data.items, mode);
    var n = items.length;

    if (summary) {
      summary.textContent = n
        ? (n + ' saved edition' + (n === 1 ? '' : 's') + '.')
        : 'Your saved releases.';
    }
    if (countEl) {
      countEl.textContent = n + ' edition' + (n === 1 ? '' : 's');
    }
    if (banner) {
      /* Notify Me state lives on each row ("Notifications coming
         shortly."); the page banner stays unused and hidden. */
      banner.hidden = true;
      banner.textContent = '';
    }

    if (!n) {
      if (empty) empty.hidden = false;
      if (list) { list.hidden = true; list.innerHTML = ''; }
      if (toolbar) toolbar.hidden = true;
      return;
    }
    if (empty) empty.hidden = true;
    if (toolbar) toolbar.hidden = false;
    if (!list) return;
    list.hidden = false;
    list.innerHTML = items.map(rowHtml).join('');
  }

  document.addEventListener('click', function (e) {
    var rem = e.target.closest('[data-tape-wish-remove]');
    if (rem) {
      e.preventDefault();
      toggle({ variant_id: rem.getAttribute('data-tape-wish-remove'), product_id: '0' });
      paintButtons();
      renderPage();
      return;
    }

    /* Notify Me is a native disabled button: it can never be the click
       target, and no alert handlers are registered anywhere. */
    var btn = e.target.closest('[data-tape-wish]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    toggle(snapshotFromButton(btn));
    paintButtons();
  });

  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches('[data-tape-wishlist-sort]')) {
      renderPage();
    }
  });

  window.addEventListener('tape:wishlist:change', function () {
    paintButtons();
    renderPage();
  });

  if (customerId && boot.seed && boot.seed.items && boot.seed.items.length) {
    var seeded = readLocal();
    if (!seeded.items.length) {
      writeLocal({
        version: boot.seed.version || 1,
        items: boot.seed.items,
        alerts: boot.seed.alerts || {},
        consent: boot.seed.consent || null
      });
    }
  }

  mergeGuestIntoCustomer();
  pullRemote().then(function () {
    /* Membership is settled (Postgres for signed-in users); now replace
       display fields with Shopify's current data before the final paint. */
    return hydrateWishlist();
  }).catch(function () { /* hydration is best-effort display enrichment */ }).finally(function () {
    paintButtons();
    paintSyncStatus();
    renderPage();
  });

  window.TapeWishlist = {
    read: readLocal,
    has: hasVariant,
    toggle: toggle,
    hydrate: hydrateWishlist,
    pageUrl: pageUrl,
    paint: paintButtons
  };
})();
